import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  exportAct1TopAchieversReport,
  exportAct2TopAchieversReport,
  setTopAchieverEventLocation,
  setTopAchieverComment,
} from "../../actions/studensts";
import { getSchools } from "../../api/index";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import TablePagination from "../../components/TablePagination";
import { excelAllTopAchievers } from "../exportToExcel";

const EVENT_LOCATIONS = ["Cairo", "Alexandria"];

const PAGE_SIZE = 100;

// Defaults to the start of the current year through today, so the page shows
// data immediately without forcing the user to pick dates first.
const defaultDateFrom = () => `${new Date().getFullYear()}-01-01`;
const defaultDateTo = () => new Date().toISOString().slice(0, 10);

// Trial/Authentication dates come back as full datetime strings; the page
// only ever shows the date part.
const formatDate = (value) => (value ? String(value).slice(0, 10) : "");

const TABS = [
  { key: "act1", label: "ACT 1 Top Achievers" },
  { key: "act2", label: "ACT 2 Top Achievers" },
];

// Editable per-row dropdown. Keyed by student id + the value it was loaded
// with, so a fresh Apply Filters fetch always starts from the server's value
// rather than a stale local edit.
function EventLocationCell({ studentId, value, onSaved, onError }) {
  const [current, setCurrent] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const next = e.target.value;
    const previous = current;
    setCurrent(next);
    setSaving(true);
    const res = await setTopAchieverEventLocation(studentId, next);
    setSaving(false);
    if (res && res.status === 200) {
      onSaved();
    } else {
      setCurrent(previous);
      onError(res?.data?.ERROR || "Failed to save event location.");
    }
  };

  return (
    <select
      value={current}
      disabled={saving}
      onChange={handleChange}
      className="border border-gray-300 rounded-lg p-1.5 text-sm
                 focus:outline-none focus:ring-2 focus:ring-blue-400
                 disabled:bg-gray-100"
    >
      <option value="">Select Location</option>
      {EVENT_LOCATIONS.map((loc) => (
        <option key={loc} value={loc}>
          {loc}
        </option>
      ))}
    </select>
  );
}

// Free-text note per student, saved explicitly so typing doesn't fire a
// request per keystroke.
function CommentCell({ studentId, value, onSaved, onError }) {
  const [text, setText] = useState(value || "");
  const [savedText, setSavedText] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const dirty = text !== savedText;

  const handleSave = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    const res = await setTopAchieverComment(studentId, text);
    setSaving(false);
    if (res && res.status === 200) {
      const stored = res.data?.comment || "";
      setText(stored);
      setSavedText(stored);
      onSaved();
    } else {
      onError(res?.data?.ERROR || "Failed to save comment.");
    }
  };

  return (
    <div className="flex items-start gap-2">
      <textarea
        rows={2}
        value={text}
        placeholder="Add a comment"
        onChange={(e) => setText(e.target.value)}
        className="border border-gray-300 rounded-lg p-2 text-sm w-56
                   focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !dirty}
        className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-medium
                   hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        {saving ? "..." : "Save"}
      </button>
    </div>
  );
}

export default function AllTopAchievers() {
  const [activeTab, setActiveTab] = useState("act1");
  const [page, setPage] = useState(1);

  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(defaultDateTo());
  const [scoreFrom, setScoreFrom] = useState("30");
  const [scoreTo, setScoreTo] = useState("36");
  const [city, setCity] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  // Client-side lookup over the rows already fetched — no refetch needed.
  const [search, setSearch] = useState("");

  const [cities, setCities] = useState([]);
  const [rows, setRows] = useState({ act1: [], act2: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flashSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };
  const flashError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 3500);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSchools();
        const uniqueCities = [
          ...new Set((data.schools || []).map((s) => s.school_city).filter(Boolean)),
        ].sort();
        setCities(uniqueCities);
      } catch {
        // City filter simply stays empty if the schools list can't be loaded.
      }
    })();
  }, []);

  // Applies the current filters to both tabs at once, so switching tabs never
  // shows results from an older filter set.
  const fetchAll = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      flashError("From and To dates are required.");
      return;
    }
    setLoading(true);
    const [resAct1, resAct2] = await Promise.all([
      exportAct1TopAchieversReport(dateFrom, dateTo, scoreFrom, scoreTo, city, eventLocation),
      exportAct2TopAchieversReport(dateFrom, dateTo, scoreFrom, scoreTo, city, eventLocation),
    ]);
    setLoading(false);

    if (resAct1?.status === 200 && resAct2?.status === 200) {
      setRows({
        act1: resAct1.data?.data || [],
        act2: resAct2.data?.data || [],
      });
      setPage(1);
    } else {
      flashError(
        resAct1?.data?.ERROR || resAct2?.data?.ERROR || "Failed to load Top Achievers."
      );
    }
  }, [dateFrom, dateTo, scoreFrom, scoreTo, city, eventLocation]);

  useEffect(() => {
    fetchAll();
    // Only run once on mount with the default filters; further loads are
    // triggered explicitly via the Apply Filters button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await excelAllTopAchievers(
        dateFrom, dateTo, scoreFrom, scoreTo, city, eventLocation, search
      );
    } catch {
      flashError("Failed to export the report.");
    }
    setExporting(false);
  };

  const currentRows = rows[activeTab];

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return currentRows;
    return currentRows.filter(
      (r) =>
        String(r["National ID"] || "").toLowerCase().includes(term) ||
        String(r["Full Name"] || "").toLowerCase().includes(term)
    );
  }, [currentRows, search]);

  // A narrowed result set can leave the current page past the end.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page]
  );

  const inputClass =
    "border border-gray-300 rounded-lg p-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400";

  return (
    <div className="px-4">
      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-800">All Top Achievers</h1>
        <p className="text-xs text-gray-500 mt-1">
          ACT 1 and ACT 2 Top Achievers, sorted by Authentication Date. Filters apply to
          both tabs; the Excel export uses the same filters and includes comments.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 bg-white border rounded-lg shadow p-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            From (Authentication Date)
          </label>
          <input
            type="date"
            className={inputClass}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            To (Authentication Date)
          </label>
          <input
            type="date"
            className={inputClass}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Grade Range From
          </label>
          <input
            type="number"
            min="1"
            max="36"
            className={inputClass}
            value={scoreFrom}
            onChange={(e) => setScoreFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Grade Range To
          </label>
          <input
            type="number"
            min="1"
            max="36"
            className={inputClass}
            value={scoreTo}
            onChange={(e) => setScoreTo(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            School City
          </label>
          <select className={inputClass} value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">All Cities</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Event Location
          </label>
          <select
            className={inputClass}
            value={eventLocation}
            onChange={(e) => setEventLocation(e.target.value)}
          >
            <option value="">All Locations</option>
            {EVENT_LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-3 lg:col-span-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || exporting}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium
                       hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="px-4 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium
                       hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Loading..." : "Apply Filters"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setActiveTab(tab.key);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === tab.key
                ? "bg-blue-800 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
        <input
          type="text"
          placeholder="Search by National ID or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto border border-gray-300 rounded-lg p-2 text-sm w-64
                     focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-semibold">
          {filteredRows.length} Results
        </span>
      </div>

      {loading ? (
        <p className="p-4 text-gray-500">Loading...</p>
      ) : filteredRows.length === 0 ? (
        <h1
          className="w-full my-2 border py-2.5 px-5 rounded-lg bg-gray-50
                     flex items-center justify-between text-sm font-medium text-red-600"
        >
          {search.trim()
            ? "No Top Achievers match your search."
            : "No Top Achievers found for the selected filters."}
        </h1>
      ) : (
        <div className="overflow-x-auto border rounded-lg shadow bg-white">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-4 py-2 border border-gray-200">National ID</th>
                <th className="px-4 py-2 border border-gray-200">Full Name</th>
                <th className="px-4 py-2 border border-gray-200">Phone Number</th>
                <th className="px-4 py-2 border border-gray-200">Event Location</th>
                {activeTab === "act1" ? (
                  <th className="px-4 py-2 border border-gray-200">Score</th>
                ) : (
                  <>
                    <th className="px-4 py-2 border border-gray-200">Subject 1</th>
                    <th className="px-4 py-2 border border-gray-200">Score 1</th>
                    <th className="px-4 py-2 border border-gray-200">Subject 2</th>
                    <th className="px-4 py-2 border border-gray-200">Score 2</th>
                    <th className="px-4 py-2 border border-gray-200">Subject 3</th>
                    <th className="px-4 py-2 border border-gray-200">Score 3</th>
                  </>
                )}
                <th className="px-4 py-2 border border-gray-200">Authentication Date</th>
                <th className="px-4 py-2 border border-gray-200">School Name</th>
                <th className="px-4 py-2 border border-gray-200">School City</th>
                <th className="px-4 py-2 border border-gray-200">Grade Status</th>
                <th className="px-4 py-2 border border-gray-200">Comment</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, i) => (
                <tr key={`${row["National ID"]}-${i}`} className="border-b border-gray-200">
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    {row["National ID"]}
                  </td>
                  <td className="px-4 py-2 border border-gray-200">{row["Full Name"]}</td>
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    {row["Phone Number"]}
                  </td>
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    <EventLocationCell
                      key={`${row["National ID"]}-${row["Event Location"] || ""}`}
                      studentId={row["National ID"]}
                      value={row["Event Location"]}
                      onSaved={() => flashSuccess(`Event location saved for ${row["Full Name"]}.`)}
                      onError={flashError}
                    />
                  </td>
                  {activeTab === "act1" ? (
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      {row["Score"]}
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-2 border border-gray-200">{row["Subject 1"]}</td>
                      <td className="px-4 py-2 border border-gray-200 text-center">
                        {row["Score 1"]}
                      </td>
                      <td className="px-4 py-2 border border-gray-200">{row["Subject 2"]}</td>
                      <td className="px-4 py-2 border border-gray-200 text-center">
                        {row["Score 2"]}
                      </td>
                      <td className="px-4 py-2 border border-gray-200">{row["Subject 3"]}</td>
                      <td className="px-4 py-2 border border-gray-200 text-center">
                        {row["Score 3"]}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    {formatDate(row["Authentication Date"])}
                  </td>
                  <td className="px-4 py-2 border border-gray-200">{row["School Name"]}</td>
                  <td className="px-4 py-2 border border-gray-200">{row["School City"]}</td>
                  <td className="px-4 py-2 border border-gray-200">{row["Grade Status"]}</td>
                  <td className="px-4 py-2 border border-gray-200">
                    <CommentCell
                      key={`${row["National ID"]}-${row["Comment"] || ""}`}
                      studentId={row["National ID"]}
                      value={row["Comment"]}
                      onSaved={() => flashSuccess(`Comment saved for ${row["Full Name"]}.`)}
                      onError={flashError}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination
        page={page}
        setPage={setPage}
        totalItems={filteredRows.length}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
}
